import React, {useContext, useState} from 'react';
import {observer} from 'mobx-react-lite';
import {
    Button,
    Grid,
    GridColumn,
    Modal,
    ModalCloser,
    ModalContent,
    ModalFooter,
    ModalHeader,
    Select,
} from '../../../patterns';
import {StoreContext} from '../base/root-store';
import {getCategoryName} from './CategoriesUtil';

export const DisableCategoryModal = observer((): JSX.Element => {
    const { questionStore: store, userStore } = useContext(StoreContext);
    const { businessUnits } = userStore.payload!;
    const { customCategoryIdInEditing: categoryId } = store.variables;

    const allBusinessUnitIds = businessUnits.map((unit) => unit.id);

    const category = store.payload?.allQuestionsWithCategories.find((cat) => cat.categoryId === categoryId);

    const initialDisabledBusinessUnits = category?.disabledBusinessUnits || [];
    const initiallyEnabledBusinessUnits = allBusinessUnitIds.filter((id) => !initialDisabledBusinessUnits.includes(id));

    const [enabledBusinessUnits, setEnabledBusinessUnits] = useState<string[]>(initiallyEnabledBusinessUnits);
    const [disabledBusinessUnits, setDisabledBusinessUnits] = useState<string[]>(initialDisabledBusinessUnits);

    const [isNoUnitsSelected, setIsNoUnitsSelected] = useState<boolean>(initialDisabledBusinessUnits.length === allBusinessUnitIds.length);

    const handleBusinessUnitChange = (value: string[] | null) => {
        if (!value) {
            return;
        }

        const includesNoUnits = value.includes('no-units');
        const selectedUnits = value.filter((id) => id !== 'no-units');

        if (includesNoUnits) {
            if (!isNoUnitsSelected) {
                setIsNoUnitsSelected(true);
                setEnabledBusinessUnits([]);
                setDisabledBusinessUnits(allBusinessUnitIds);
            } else {
                setIsNoUnitsSelected(false);
                setEnabledBusinessUnits(selectedUnits);
                setDisabledBusinessUnits(allBusinessUnitIds.filter((id) => !selectedUnits.includes(id)));
            }
        } else {
            if (isNoUnitsSelected) {
                setIsNoUnitsSelected(false);
                setEnabledBusinessUnits(allBusinessUnitIds);
                setDisabledBusinessUnits([]);
            } else {
                setEnabledBusinessUnits(selectedUnits);
                setDisabledBusinessUnits(allBusinessUnitIds.filter((id) => !selectedUnits.includes(id)));
            }
        }
    };


    const handleSave = () => {
        const newlyDisabledUnits = disabledBusinessUnits.filter((id) => initiallyEnabledBusinessUnits.includes(id));
        const newlyEnabledUnits = enabledBusinessUnits.filter((id) => !initiallyEnabledBusinessUnits.includes(id));

        store.disableCategoryForBusinessUnit(newlyDisabledUnits, categoryId!, true);
        store.disableCategoryForBusinessUnit(newlyEnabledUnits, categoryId!, false);
    };

    const label = getCategoryName(userStore.payload!.language, categoryId!, store.categories);
    const unitLabel = `${store.label('questions.catalog-questions.sub-header.team-usage')} ${enabledBusinessUnits.length}/${allBusinessUnitIds.length}`;

    return (
        <Modal size="small">
            <ModalHeader>
                <h4>{label}</h4>
            </ModalHeader>
            <ModalContent>
                <Select
                    label={unitLabel}
                    value={isNoUnitsSelected ? ['no-units'] : enabledBusinessUnits}
                    onChange={handleBusinessUnitChange}
                    options={[
                        { label: store.label('company.matrix-team-modal.no-children'), value: 'no-units' },
                        ...businessUnits.map((unit) => ({
                            label: unit.name,
                            value: unit.id,
                        })),
                    ]}
                    search={{
                        label: store.label('common.filter.search.label'),
                        id: 'teams-search',
                        name: 'teams-search',
                    }}
                    id="child-teams"
                    name="child-teams"
                    type="multiple"
                />
            </ModalContent>
            <ModalFooter>
                <Grid wrap={['xs']} justify={['between-xs']}>
                    <GridColumn width={['xs-12', 'sm-6']}>
                        <ModalCloser>
                            <Button block color="white" text={store.label('company.modal.cancel')} />
                        </ModalCloser>
                    </GridColumn>
                    <GridColumn width={['xs-12', 'sm-6']}>
                        <Button block text={store.label('company.modal.save')} onClick={handleSave} />
                    </GridColumn>
                </Grid>
            </ModalFooter>
        </Modal>
    );
});
